/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {z} from '@hono/zod-openapi';

const nullableString = z.string().min(1).nullable();
const eurotaxExternalIdSchema = z.string().regex(/^\d+$/);

export const automarketplEurotaxRuleSignatureSchema = z
  .object({
    make: z.string().min(1),
    modelFamily: z.string().min(1),
    generation: nullableString,
    version: nullableString,
    trim: z.string().min(1).optional(),
    firstRegistrationOn: z.string().date(),
    fuelType: nullableString,
    hybridType: nullableString,
    engineVolume: z.number().finite().nullable(),
    maximalPower: z.number().finite().nullable(),
    transmission: nullableString,
    bodyStyle: nullableString,
    drive: nullableString,
  })
  .openapi('AutomarketplEurotaxRuleSignature');

export const activateAutomarketplEurotaxMappingRuleRequestSchema = z
  .object({
    signature: automarketplEurotaxRuleSignatureSchema,
    eurotaxCode: z.number().int().positive(),
    candidateExternalIds: z.array(eurotaxExternalIdSchema).min(2),
    reason: z.string().trim().max(500).default(''),
  })
  .superRefine(({candidateExternalIds, eurotaxCode}, context) => {
    if (new Set(candidateExternalIds).size !== candidateExternalIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['candidateExternalIds'],
        message: 'candidateExternalIds must be unique',
      });
    }

    if (!candidateExternalIds.includes(String(eurotaxCode))) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eurotaxCode'],
        message: 'eurotaxCode must be one of candidateExternalIds',
      });
    }
  })
  .openapi('ActivateAutomarketplEurotaxMappingRuleRequest');

export const automarketplEurotaxMappingRuleSchema = z
  .object({
    id: z.string().uuid(),
    signatureHash: z.string().startsWith('sha256:'),
    signature: automarketplEurotaxRuleSignatureSchema,
    eurotaxCode: z.number().int().positive(),
    candidateExternalIds: z.array(eurotaxExternalIdSchema),
    enabled: z.boolean(),
    reason: z.string(),
    createdBy: z.string(),
    createdAt: z.string().datetime(),
    disabledBy: z.string().nullable(),
    disabledAt: z.string().datetime().nullable(),
  })
  .openapi('AutomarketplEurotaxMappingRule');

export const automarketplEurotaxMappingRulesQuerySchema = z.object({
  enabled: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
});

export const automarketplEurotaxMappingRulesResponseSchema = z
  .array(automarketplEurotaxMappingRuleSchema)
  .openapi('AutomarketplEurotaxMappingRulesResponse');

export type ActivateAutomarketplEurotaxMappingRuleRequest = z.infer<
  typeof activateAutomarketplEurotaxMappingRuleRequestSchema
>;
export type AutomarketplEurotaxRuleSignature = z.infer<
  typeof automarketplEurotaxRuleSignatureSchema
>;
export type AutomarketplEurotaxMappingRule = z.infer<typeof automarketplEurotaxMappingRuleSchema>;
